import React, { useState } from "react";
import { Building2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  formatPropertyStatus,
  formatPropertyPrice,
} from "../../../utils/formatters";
import { canFeatureProperties } from "../../../constants/plans";

export const PropertiesSection = ({
  properties,
  deletedProperties = [],
  currentUser,
  sectionTitle = "Mis Propiedades",
  navigate,
  handleDeleteProperty,
  handleRestoreProperty,
  handleToggleActive,
  handleToggleFeatured,
}) => {
  const [activeTab, setActiveTab] = useState("active");
  const visibleProperties =
    activeTab === "deleted"
      ? deletedProperties
      : properties.filter((p) =>
          activeTab === "active"
            ? p.is_active !== false
            : p.is_active === false,
        );

  return (
    <AccordionItem
      value="properties"
      className="bg-background border border-border rounded-lg"
    >
      <AccordionTrigger className="px-4 sm:px-6 py-4 hover:no-underline">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-primary" />
          <span className="font-bold text-base">
            {sectionTitle} ({properties.length})
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="px-4 sm:px-6 pb-4">
          <div className="flex gap-2 mb-4">
            <Button
              variant={activeTab === "active" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("active")}
            >
              Activas ({properties.filter((p) => p.is_active !== false).length})
            </Button>
            <Button
              variant={activeTab === "archived" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("archived")}
            >
              Archivadas (
              {properties.filter((p) => p.is_active === false).length})
            </Button>
            <Button
              variant={activeTab === "deleted" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("deleted")}
            >
              Eliminadas ({deletedProperties.length})
            </Button>
          </div>
          <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
            {visibleProperties.map((property) => (
                <div key={property.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div
                      className="w-full sm:w-32 h-24 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
                      onClick={() => navigate(`/property/${property.id}`)}
                    >
                      <img
                        src={
                          property.image_urls?.[0] ||
                          "https://images.pexels.com/photos/35193584/pexels-photo-35193584.jpeg"
                        }
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3
                            className="font-bold text-sm sm:text-base truncate cursor-pointer hover:text-primary"
                            onClick={() => navigate(`/property/${property.id}`)}
                          >
                            {property.title}
                          </h3>
                          <p className="text-xs text-muted-foreground truncate">
                            {property.address}
                          </p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                              {property.category}
                            </span>
                            <span className="text-xs bg-muted px-2 py-0.5 rounded">
                              {formatPropertyStatus(property.status)}
                            </span>
                            {property.is_featured && (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                                Destacada
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-sm font-bold whitespace-nowrap">
                          {formatPropertyPrice(property)}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {property.view_count || 0}
                        </span>
                        {property.co_listing_enabled && (
                          <span className="text-primary">
                            Co-listing: {property.broker_slots || 0} slots
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {activeTab === "deleted" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRestoreProperty(property.id)}
                          >
                            Restaurar
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                navigate(`/edit-property/${property.id}`)
                              }
                            >
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleToggleActive(property.id, property.is_active)
                              }
                            >
                              {property.is_active !== false
                                ? "Archivar"
                                : "Activar"}
                            </Button>
                            {canFeatureProperties(currentUser) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleToggleFeatured(property.id)}
                              >
                                {property.is_featured
                                  ? "Quitar destacado"
                                  : "Destacar"}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive"
                              onClick={() => handleDeleteProperty(property.id)}
                            >
                              Eliminar
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            {visibleProperties.length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  {activeTab === "active"
                    ? "No tienes propiedades activas"
                    : activeTab === "archived"
                      ? "No hay propiedades archivadas"
                      : "No hay propiedades eliminadas"}
                </p>
              </div>
            )}
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
